import React from "react";
import { LineChart, Wallet, TrendingUp, AlertTriangle } from "lucide-react";
import { fmtMoney } from "../lib/money";
import { useTranslation } from "react-i18next";

const fmtRatio = (n) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(2));
const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(2)}%`);
const makeMoneyFmt = (currency) => (n) => {
  const f = fmtMoney(n, currency);
  return f == null ? "—" : f;
};

// Visual cue thresholds tuned for typical small-business covenants
const ratioTone = (key, v) => {
  if (v == null) return "zinc";
  if (key === "current_ratio") return v >= 1.5 ? "emerald" : v >= 1.0 ? "amber" : "rose";
  if (key === "dscr") return v >= 1.25 ? "emerald" : v >= 1.0 ? "amber" : "rose";
  if (key === "debt_to_ebitda") return v <= 3 ? "emerald" : v <= 4 ? "amber" : "rose";
  if (key === "fixed_charge_coverage") return v >= 1.25 ? "emerald" : v >= 1.0 ? "amber" : "rose";
  return "zinc";
};

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  zinc: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

// Returns the row spec. Money columns use the enrichment's native currency.
const buildMetricRows = (currency) => {
  const money = makeMoneyFmt(currency);
  return [
    ["annual_revenue", money],
    ["monthly_revenue", money],
    ["annual_ebitda", money],
    ["monthly_rent", money],
    ["annual_debt_service", money],
    ["monthly_debt_payments", money],
    ["cash_position", money],
    ["current_assets", money],
    ["current_liabilities", money],
    ["total_assets", money],
    ["total_liabilities", money],
    ["tangible_net_worth", money],
    ["gross_margin_pct", fmtPct],
    ["operating_margin_pct", fmtPct],
  ];
};

const RATIO_KEYS = ["current_ratio", "dscr", "debt_to_ebitda", "fixed_charge_coverage"];

export default function FinancialsEnrichmentPanel({ enrichment }) {
  const { t } = useTranslation("components");
  const e = enrichment || {};
  const metrics = e.metrics || {};
  const ratios = e.ratios || {};
  const currency = (e.currency || "USD").toUpperCase();
  const METRIC_ROWS = buildMetricRows(currency);
  const hasAnyMetric = METRIC_ROWS.some(([k]) => metrics[k] != null);

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6"
      data-testid="financials-enrichment-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-md grid place-items-center bg-indigo-50 text-indigo-700">
          <LineChart size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">
          {t("enrichment.financials.section_title")}
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile
          label={t("enrichment.financials.statement_type")}
          value={e.statement_type ? t(`enrichment.financials.statement_type_options.${e.statement_type}`, e.statement_type.replace(/_/g, " ")) : "—"}
          testid="fin-stmt-type"
        />
        <Tile label={t("enrichment.financials.period")} value={e.period_label || "—"} testid="fin-stmt-period" />
        <Tile label={t("enrichment.financials.currency")} value={currency} testid="fin-stmt-currency" />
        <Tile
          label={t("enrichment.financials.range")}
          value={
            e.period_start || e.period_end
              ? `${e.period_start || "—"} → ${e.period_end || "—"}`
              : "—"
          }
          testid="fin-stmt-range"
        />
      </div>

      {hasAnyMetric && (
        <div className="mb-6" data-testid="fin-metrics-block">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1">
            <Wallet size={11} />
            {t("enrichment.financials.business_metrics_label")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {METRIC_ROWS.filter(([k]) => metrics[k] != null).map(([k, fmt]) => (
              <div
                key={k}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200 text-sm"
                data-testid={`fin-metric-${k}`}
              >
                <span className="text-zinc-600">{t(`enrichment.financials.metrics.${k}`)}</span>
                <span className="font-mono tabular-nums text-zinc-950">{fmt(metrics[k])}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-5" data-testid="fin-ratios-block">
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1">
          <TrendingUp size={11} />
          {t("enrichment.financials.key_ratios_label")}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {RATIO_KEYS.map((k) => {
            const v = ratios[k];
            const tone = ratioTone(k, v);
            return (
              <div
                key={k}
                className={`px-3 py-3 rounded-lg border ${TONES[tone]}`}
                data-testid={`fin-ratio-${k}`}
              >
                <p className="text-[10px] uppercase tracking-wider opacity-80">{t(`enrichment.financials.ratios.${k}`)}</p>
                <p className="font-mono tabular-nums text-lg font-semibold mt-0.5">
                  {fmtRatio(v)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {e.notes?.length > 0 && (
        <div className="mb-3" data-testid="fin-notes">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.financials.notes_label")}
          </p>
          <ul className="space-y-1">
            {e.notes.map((n, i) => (
              <li
                key={i}
                className="text-sm text-zinc-800 leading-snug flex gap-1.5 px-3 py-1.5 rounded bg-amber-50/40 border border-amber-100"
                data-testid={`fin-note-${i}`}
              >
                <AlertTriangle size={12} className="text-amber-700 shrink-0 mt-0.5" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {e.summary && (
        <p className="text-sm text-zinc-700 mt-3" data-testid="fin-stmt-summary">
          {e.summary}
        </p>
      )}
    </section>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</p>
      <p className="font-display font-semibold text-zinc-950 break-words">{value}</p>
    </div>
  );
}
